import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, Button } from "@/components/atoms";
import { useFilterCompanies, useFilterJobs } from "@/providers/contexts";
import { DomainFilter } from "@/components/organisms/Filters";
import { FilterPanelProps } from "./FilterPanel";
import { debounce } from "lodash";
import { CompanyFiltersState } from "@/providers/contexts/FilterCompaniesContext";
import { JobPostFiltersState } from "@/providers/contexts/FilterJobsContext";

interface SearchPanelProps {
  renderDomainFilter?: boolean;
  renderSubmitButton?: boolean;
  page: FilterPanelProps["page"];
}

export const SearchPanel = ({
  renderDomainFilter = false,
  renderSubmitButton = false,
  page,
}: SearchPanelProps) => {
  const { filters: jobFilters, dispatch: jobDispatch } = useFilterJobs();
  const { filters: companyFilters, dispatch: companyDispatch } =
    useFilterCompanies();

  const componentName = "search-panel";
  const isSearchPage = page === "search";

  const { search } = isSearchPage ? jobFilters : companyFilters;
  const dispatch = isSearchPage ? jobDispatch : companyDispatch;

  const [localSearch, setLocalSearch] = useState<
    CompanyFiltersState["search"] | JobPostFiltersState["search"]
  >(search ?? "");

  useEffect(() => {
    setLocalSearch(search ?? "");
  }, [search]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchVal: string) => {
        dispatch({
          type: "SET_FILTERS",
          payload: {
            search: searchVal,
          },
        });
      }, 500),
    [dispatch],
  );

  useEffect(() => {
    if (localSearch !== search && localSearch !== undefined) {
      debouncedSearch(localSearch);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [localSearch, debouncedSearch, search]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    const newFilters = {
      search: localSearch,
      selectedHealthcareDomains: jobFilters.selectedHealthcareDomains,
    };

    sessionStorage.setItem(
      "filters",
      JSON.stringify({ ...jobFilters, ...newFilters }),
    );

    dispatch({
      type: "SET_FILTERS",
      payload: newFilters,
    });

    window.location.href = `/search`;
  };

  return (
    <Box
      componentName={componentName}
      border={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        componentName={componentName}
        border={false}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "nowrap",
          width: "100%",
        }}
        columnGap={2}
      >
        <TextField
          variant="outlined"
          componentName={componentName}
          placeholder="Search for jobs or companies..."
          value={localSearch}
          onChange={handleSearchChange}
          sx={{
            flex: renderDomainFilter ? 2 : 1,
            minWidth: renderDomainFilter ? "66.66%" : "100%",
            width: "100%",
            mb: 0,
          }}
        />
        {renderDomainFilter && (
          <DomainFilter
            componentName={componentName}
            boxSx={{
              mt: 0,
              flex: 1,
              minWidth: "33.33%",
              display: "flex",
              alignItems: "center",
            }}
            autocompleteSx={{
              mt: 0,
            }}
            page={page}
          />
        )}
      </Box>
      {renderSubmitButton && (
        <Button
          componentName={componentName + "-submit-button"}
          color="primary"
          sx={{
            mt: 4,
            width: "100%",
          }}
          onClick={handleSearchSubmit}
        >
          Search
        </Button>
      )}
    </Box>
  );
};
